import { useState, useEffect, useRef } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Activity, RefreshCw, ShieldOff, Trash2, Zap } from "lucide-react";

const BUCKET_LABELS = {
  user_cache:          { label: "Session cache",      hint: "/auth/me cached users (60s TTL)" },
  jti_revoked:         { label: "Revoked JWTs",       hint: "Tokens blacklisted after logout" },
  rate_limit_login:    { label: "Login throttles",    hint: "IPs hitting failed-login limiter" },
  rate_limit_register: { label: "Register throttles", hint: "IPs hitting signup limiter" },
  rate_limit_phoneotp: { label: "Phone-OTP quotas",   hint: "Phones at OTP-send limit" },
  pwreset_pending:     { label: "Password resets",    hint: "Active reset OTPs (10min TTL)" },
};

const BUCKET_STYLES = {
  login:    "bg-[#FFEFEF] text-[#7A1F1F]",
  register: "bg-[#FFF3D6] text-[#5C4400]",
  phoneotp: "bg-[#E8F0FE] text-[#1A4480]",
};

function fmtTTL(sec) {
  if (sec <= 0) return "expired";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
}

export default function RedisStatsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const timerRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/system/redis-stats");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Redis stats");
      setData({ connected: false, message: e.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 15000); // auto-refresh every 15s
    return () => clearInterval(timerRef.current);
  }, []);

  const clearThrottle = async (bucket, identifier) => {
    const key = `${bucket}:${identifier}`;
    setBusy({ ...busy, [key]: true });
    try {
      await api.post("/admin/system/clear-rate-limit", { bucket, identifier });
      toast.success(`Unblocked ${identifier} from ${bucket}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear rate limit");
    } finally {
      setBusy({ ...busy, [key]: false });
    }
  };

  const revokeSessions = async () => {
    const user_id = window.prompt("Force-logout: enter the User ID to revoke all sessions for");
    if (!user_id) return;
    try {
      const r = await api.post("/admin/system/revoke-user-sessions", { user_id: user_id.trim() });
      toast.success(`Revoked sessions for ${r.data.user_email}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke sessions");
    }
  };

  if (!data) {
    return <div className="p-6 text-[#8A998E]">Loading Redis stats…</div>;
  }

  if (!data.connected) {
    return (
      <div className="p-6 rounded-lg bg-[#FFF3D6] border border-[#E5D5A0]" data-testid="redis-offline-warning">
        <div className="font-semibold text-[#5C4400]">Redis is not connected</div>
        <div className="text-sm text-[#5C4400] mt-1">{data.message || "Set REDIS_URL in /var/www/api/.env and restart the API."}</div>
      </div>
    );
  }

  const memPct = data.memory?.max_mb ? Math.round((data.memory.used_mb / data.memory.max_mb) * 100) : null;
  const hitRate = (() => {
    const h = data.stats?.keyspace_hits || 0;
    const m = data.stats?.keyspace_misses || 0;
    if (h + m === 0) return null;
    return Math.round((h / (h + m)) * 100);
  })();
  const blockedCount = (data.throttled || []).filter((t) => t.blocked).length;

  return (
    <div className="p-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1A231C] flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#2C5F2D]" />
            Redis Observability
          </h2>
          <p className="text-sm text-[#8A998E] mt-1">
            Sessions cache · JWT revocation list · Rate limits · OTP store · Auto-refresh every 15s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={revokeSessions} data-testid="redis-revoke-sessions-btn">
            <ShieldOff className="w-4 h-4 mr-2" /> Force Logout User
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="redis-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Health tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="p-4 rounded-lg bg-[#DDF0E0] text-[#1F4D2C]" data-testid="redis-memory-tile">
          <div className="text-xs uppercase tracking-wider opacity-70">Memory</div>
          <div className="text-2xl font-bold mt-1">{data.memory?.used_mb ?? "—"} MB</div>
          <div className="text-xs opacity-70 mt-1">
            {data.memory?.max_mb ? `${memPct}% of ${data.memory.max_mb} MB` : "no limit set"}
          </div>
        </div>
        <div className="p-4 rounded-lg bg-[#E8F0FE] text-[#1A4480]" data-testid="redis-clients-tile">
          <div className="text-xs uppercase tracking-wider opacity-70">Connected Clients</div>
          <div className="text-2xl font-bold mt-1">{data.clients?.connected ?? 0}</div>
          <div className="text-xs opacity-70 mt-1">{data.stats?.ops_per_sec ?? 0} ops/sec</div>
        </div>
        <div className="p-4 rounded-lg bg-[#FFF3D6] text-[#5C4400]" data-testid="redis-hitrate-tile">
          <div className="text-xs uppercase tracking-wider opacity-70">Cache Hit Rate</div>
          <div className="text-2xl font-bold mt-1">{hitRate !== null ? `${hitRate}%` : "—"}</div>
          <div className="text-xs opacity-70 mt-1">
            {(data.stats?.keyspace_hits ?? 0).toLocaleString()} hits
          </div>
        </div>
        <div className={`p-4 rounded-lg ${blockedCount > 0 ? "bg-[#FFEFEF] text-[#7A1F1F]" : "bg-[#F3F2EE] text-[#1A231C]"}`} data-testid="redis-blocked-tile">
          <div className="text-xs uppercase tracking-wider opacity-70">Currently Blocked</div>
          <div className="text-2xl font-bold mt-1">{blockedCount}</div>
          <div className="text-xs opacity-70 mt-1">IPs / phones rate-limited</div>
        </div>
      </div>

      {/* Bucket counts */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-[#1A231C] mb-3">Key namespace breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(BUCKET_LABELS).map(([k, meta]) => (
            <div key={k} className="p-3 rounded-lg border border-[#E5E1DA] bg-white" data-testid={`redis-bucket-${k}`}>
              <div className="flex items-baseline justify-between">
                <div className="text-xs uppercase tracking-wider text-[#8A998E]">{meta.label}</div>
                <div className="text-xl font-bold text-[#1A231C]">{data.buckets[k] ?? 0}</div>
              </div>
              <div className="text-xs text-[#8A998E] mt-1">{meta.hint}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Throttled list */}
      <div>
        <h3 className="text-sm font-semibold text-[#1A231C] mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#7A1F1F]" />
          Live throttle activity ({(data.throttled || []).length})
        </h3>
        {(data.throttled || []).length === 0 ? (
          <div className="p-6 text-center text-[#8A998E] border border-dashed border-[#E5E1DA] rounded-lg">
            No rate-limit activity right now. 👍
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-[#E5E1DA]">
            <table className="w-full text-sm">
              <thead className="bg-[#F3F2EE] text-[#1A231C]">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Bucket</th>
                  <th className="text-left px-4 py-2 font-semibold">Identifier</th>
                  <th className="text-left px-4 py-2 font-semibold">Hits</th>
                  <th className="text-left px-4 py-2 font-semibold">TTL</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                  <th className="text-right px-4 py-2 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {(data.throttled || []).map((t, i) => {
                  const key = `${t.bucket}:${t.identifier}`;
                  return (
                    <tr key={key + i} className="border-t border-[#E5E1DA] hover:bg-[#FAFAF7]"
                      data-testid={`redis-throttle-row-${i}`}>
                      <td className="px-4 py-2">
                        <Badge className={`${BUCKET_STYLES[t.bucket] || "bg-gray-100"} border-0`}>{t.bucket}</Badge>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-[#1A231C]">{t.identifier}</td>
                      <td className="px-4 py-2 text-xs">
                        <span className={t.blocked ? "text-[#7A1F1F] font-bold" : "text-[#8A998E]"}>
                          {t.count} / {t.limit}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-[#8A998E]">{fmtTTL(t.ttl_sec)}</td>
                      <td className="px-4 py-2 text-xs">
                        {t.blocked
                          ? <span className="text-[#7A1F1F] font-semibold">🚫 BLOCKED</span>
                          : <span className="text-[#1F4D2C]">tracking</span>}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                          disabled={busy[key]}
                          onClick={() => clearThrottle(t.bucket, t.identifier)}
                          data-testid={`redis-clear-throttle-${i}`}>
                          <Trash2 className="w-3 h-3 mr-1" /> Clear
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-6 text-xs text-[#8A998E]">
        <strong>Force Logout User</strong>: stamps <code>token_revoked_at</code> on the user record →
        every existing JWT for that user is rejected by <code>get_current_user</code> on the next request.
        Use when a user reports account compromise.
        <strong className="ml-3">Clear</strong>: removes a single rate-limit counter so a legitimate user can retry immediately.
      </div>
    </div>
  );
}
